import { useState, useMemo } from "react";
import { useQuery, useMutation } from "react-query";
import type { ColumnDef, ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatCurrency } from "@/lib/utils";
import {
  Wallet,
  DollarSign,
  AlertCircle,
  Calendar,
  Coins,
  CreditCard,
  CheckCircle2,
  Clock,
  Info,
  RotateCcw,
  Loader2,
  CalendarClock,
  CalendarDays,
  StickyNote,
  TrendingUp,
  X,
  HandCoins,
} from "lucide-react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const NORMALIZED_APPROVED = ["Approved", "approved"];
const NORMALIZED_REJECTED = ["Rejected", "rejected"];

function normalizeDeclarationStatus(s: string): "pending" | "approved" | "rejected" {
  const st = String(s || "").trim().toLowerCase();
  if (st === "approved" || st === "paid") return "approved";
  if (st === "rejected") return "rejected";
  return "pending";
}

function safeNumber(v: any): number {
  if (v == null) return 0;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function getLoanRemaining(loan: any): number {
  const amount = safeNumber(loan.amount);
  const totalPaid = safeNumber(loan.totalPaid);
  const dueAmount = safeNumber(loan.dueAmount);
  return dueAmount || (amount - totalPaid);
}

export default function DeclareLoanPayment() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [paymentAmountError, setPaymentAmountError] = useState<string>("");
  const [selectedLoanId, setSelectedLoanId] = useState<number | null>(null);
  const [deductionMonth, setDeductionMonth] = useState<string>("");
  const [deductionDate, setDeductionDate] = useState<string>("");
  const [note, setNote] = useState<string>("");

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const selectedMonth = currentMonth;
  const selectedYear = currentYear;

  // ── DataTable state for Payment History ──────────────────────────────
  const [historySearch, setHistorySearch] = useState("");
  const [historyColumnFilters, setHistoryColumnFilters] = useState<ColumnFiltersState>([]);
  const [historySorting, setHistorySorting] = useState<SortingState>([{ id: "createdAt", desc: true }]);
  const [historyPagination, setHistoryPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });

  // ── DataTable state for Active Loans ─────────────────────────────────
  const [loansSearch, setLoansSearch] = useState("");
  const [loansColumnFilters, setLoansColumnFilters] = useState<ColumnFiltersState>([]);
  const [loansSorting, setLoansSorting] = useState<SortingState>([{ id: "id", desc: true }]);
  const [loansPagination, setLoansPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });

  // ── Data Fetching ────────────────────────────────────────────────────
  const { data: loansData, isLoading: loadingLoans, isFetching: fetchingLoans } = useQuery(
    ["my-loans", user?.id],
    async () => {
      if (!user?.id) return { loans: [] };
      const { data } = await api.get(`/teachers/${user?.id}/loans`);
      const responseData = data.data || {};
      return { loans: Array.isArray(responseData.loans) ? responseData.loans : [] };
    },
    { enabled: !!user?.id, staleTime: 30000 }
  );

  const { data: declarationStatus, refetch: refetchStatus } = useQuery(
    ["loan-payment-declaration-status", user?.id],
    async () => {
      if (!user?.id) return null;
      const { data } = await api.get(`/loan-payment-declarations/member/${user.id}/status`);
      return data.data;
    },
    { enabled: !!user?.id }
  );

  const {
    data: paymentHistoryRaw,
    isLoading: loadingHistory,
    isFetching: fetchingHistory,
  } = useQuery(
    ["loan-payment-history", user?.id],
    async () => {
      if (!user?.id) return [];
      const { data } = await api.get(`/loan-payment-declarations/member/${user.id}`);
      return data.data || data || [];
    },
    { enabled: !!user?.id, staleTime: 30000 }
  );

  // ── Active Loans ─────────────────────────────────────────────────────
  const activeLoans = useMemo(() => {
    const loans = loansData?.loans || [];
    return loans.filter((loan: any) => {
      const status = String(loan.status || "").toLowerCase();
      const isActiveOrApproved = ["active", "approved"].includes(status);
      if (!isActiveOrApproved) return false;
      return getLoanRemaining(loan) > 0;
    });
  }, [loansData]);

  const totalOutstanding = useMemo(() => {
    return activeLoans.reduce((sum: number, loan: any) => sum + getLoanRemaining(loan), 0);
  }, [activeLoans]);

  const selectedLoan = useMemo(() => {
    if (!selectedLoanId) return null;
    return activeLoans.find((loan: any) => loan.id === selectedLoanId);
  }, [selectedLoanId, activeLoans]);

  const maxPaymentAmount = useMemo(() => {
    if (selectedLoan) return getLoanRemaining(selectedLoan);
    return totalOutstanding;
  }, [selectedLoan, totalOutstanding]);

  const monthlyInstallment = useMemo(() => {
    if (!selectedLoan) return 0;
    return safeNumber(selectedLoan.monthlyPayment);
  }, [selectedLoan]);

  const nextPaymentDate = useMemo(() => {
    if (activeLoans.length === 0) return "N/A";
    const dates = activeLoans.map((loan: any) => {
      if (loan.nextPaymentDate) return new Date(loan.nextPaymentDate);
      return new Date(currentYear, currentMonth + 1, 1);
    });
    const earliest = dates.reduce((min: Date, d: Date) => d < min ? d : min, dates[0]);
    return earliest.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }, [activeLoans, currentMonth, currentYear]);

  const hasExistingDeclaration = declarationStatus?.hasDeclaration;
  const canDeclare = declarationStatus?.canDeclare !== false;
  const hasActiveLoans = activeLoans.length > 0;
  const canDeclareWithLoans = canDeclare || hasActiveLoans;

  // ── Declaration Mutation ─────────────────────────────────────────────
  const declareMutation = useMutation(
    async () => {
      if (!user?.id) throw new Error("User not found");
      if (!paymentAmount || parseFloat(paymentAmount) <= 0) throw new Error("Please enter a valid payment amount");
      const amount = parseFloat(paymentAmount);
      if (amount > maxPaymentAmount) throw new Error(`Amount cannot exceed ${maxPaymentAmount.toLocaleString()} FRW`);
      const payload: any = {
        memberId: user.id,
        loanId: selectedLoanId || undefined,
        paymentAmount: amount,
        month: selectedMonth,
        year: selectedYear,
      };
      if (note.trim()) payload.notes = note.trim();
      const { data } = await api.post("/loan-payment-declarations", payload);
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Loan payment declared successfully!", {
          description: `Your payment of ${parseFloat(paymentAmount).toLocaleString()} FRW will be deducted from your salary.`,
        });
        setPaymentAmount("");
        setPaymentAmountError("");
        setSelectedLoanId(null);
        setDeductionMonth("");
        setDeductionDate("");
        setNote("");
        refetchStatus();
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to declare loan payment";
        if (errorMessage.includes("already declared") || errorMessage.includes("once per month")) {
          toast.error("Declaration already exists", { description: errorMessage, duration: 5000 });
          refetchStatus();
        } else if (error?.message?.includes("cannot exceed")) {
          setPaymentAmountError(error.message);
          toast.error(errorMessage);
        } else {
          toast.error(errorMessage);
        }
      },
    }
  );

  // ── Form Handlers ────────────────────────────────────────────────────
  const handlePaymentAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") { setPaymentAmount(""); setPaymentAmountError(""); return; }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) { setPaymentAmountError("Please enter a valid number"); return; }
    if (numValue <= 0) { setPaymentAmountError("Amount must be greater than 0"); return; }
    if (numValue > maxPaymentAmount) { setPaymentAmount(maxPaymentAmount.toString()); setPaymentAmountError(""); return; }
    setPaymentAmount(value);
    setPaymentAmountError("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) { toast.error("Please enter a valid payment amount"); return; }
    const amount = parseFloat(paymentAmount);
    if (amount > maxPaymentAmount) {
      toast.error(`Amount cannot exceed ${maxPaymentAmount.toLocaleString()} FRW`);
      setPaymentAmountError(`Amount cannot exceed ${maxPaymentAmount.toLocaleString()} FRW`);
      return;
    }
    declareMutation.mutate();
  };

  const handleClear = () => {
    setPaymentAmount("");
    setPaymentAmountError("");
    setSelectedLoanId(null);
    setDeductionMonth("");
    setDeductionDate("");
    setNote("");
  };

  // Revalidate on maxPaymentAmount change
  useMemo(() => {
    if (paymentAmount) {
      const numValue = parseFloat(paymentAmount);
      if (!isNaN(numValue) && numValue > maxPaymentAmount) {
        setPaymentAmountError(`Amount cannot exceed ${maxPaymentAmount.toLocaleString()} FRW`);
      } else if (!isNaN(numValue) && numValue > 0 && numValue <= maxPaymentAmount) {
        setPaymentAmountError("");
      }
    }
  }, [maxPaymentAmount]);

  // ── Preview Computed Values ──────────────────────────────────────────
  const declaredAmount = paymentAmount ? parseFloat(paymentAmount) : 0;
  const loanAmount = selectedLoan ? safeNumber(selectedLoan.amount) : 0;
  const paidAmount = selectedLoan ? safeNumber(selectedLoan.totalPaid) : 0;
  const repaymentProgress = loanAmount > 0 ? (paidAmount / loanAmount) * 100 : 0;
  const balanceAfter = (selectedLoan ? getLoanRemaining(selectedLoan) : totalOutstanding) - declaredAmount;

  // ── Active Loans Table Data ──────────────────────────────────────────
  const loansRowData = useMemo(() => {
    return activeLoans.map((loan: any) => {
      const amount = safeNumber(loan.amount);
      const disbursed = safeNumber(loan.disbursedAmount) || amount;
      const totalPaid = safeNumber(loan.totalPaid);
      const remaining = getLoanRemaining(loan);
      const monthly = safeNumber(loan.monthlyPayment);
      const progress = amount > 0 ? (totalPaid / amount) * 100 : 0;
      const status = String(loan.status || "").toLowerCase();
      const nextDate = loan.nextPaymentDate
        ? new Date(loan.nextPaymentDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : nextPaymentDate;

      return {
        ...loan,
        amountValue: amount,
        disbursedValue: disbursed,
        totalPaidValue: totalPaid,
        remainingValue: remaining,
        monthlyValue: monthly,
        progressValue: progress,
        statusGroup: status,
        statusLabel: loan.status || "Active",
        purposeLabel: loan.purpose || "General",
        nextDateLabel: nextDate,
        isSelected: selectedLoanId === loan.id,
      };
    });
  }, [activeLoans, nextPaymentDate, selectedLoanId]);

  const filteredLoans = useMemo(() => {
    const q = loansSearch.trim().toLowerCase();
    const statusFilter = loansColumnFilters.find((f) => f.id === "statusGroup")?.value as string[] | undefined;

    const list = loansRowData.filter((row: any) => {
      const matchesSearch = !q || [
        row.purposeLabel, row.statusLabel, row.id,
        row.amountValue, row.remainingValue,
      ].join(" ").toLowerCase().includes(q);
      const matchesStatus = !statusFilter?.length || statusFilter.includes(row.statusGroup);
      return matchesSearch && matchesStatus;
    });

    const sort = loansSorting[0] || { id: "id", desc: true };
    return [...list].sort((a: any, b: any) => {
      let av = a[sort.id];
      let bv = b[sort.id];
      if (typeof av === "number" && typeof bv === "number") return sort.desc ? bv - av : av - bv;
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av === bv) return 0;
      return sort.desc ? (av > bv ? -1 : 1) : (av > bv ? 1 : -1);
    });
  }, [loansRowData, loansSearch, loansColumnFilters, loansSorting]);

  const loansPageCount = Math.max(1, Math.ceil(filteredLoans.length / loansPagination.pageSize));
  const paginatedLoans = useMemo(() => {
    const safePageIndex = Math.min(loansPagination.pageIndex, loansPageCount - 1);
    const start = safePageIndex * loansPagination.pageSize;
    return filteredLoans.slice(start, start + loansPagination.pageSize);
  }, [filteredLoans, loansPageCount, loansPagination.pageIndex, loansPagination.pageSize]);

  // ── Payment History Table Data ───────────────────────────────────────
  const historyRowData = useMemo(() => {
    if (!paymentHistoryRaw || !Array.isArray(paymentHistoryRaw)) return [];
    return paymentHistoryRaw.map((d: any) => {
      const statusGroup = normalizeDeclarationStatus(d.groupLeaderApproved ? "approved" : d.isProcessed ? "paid" : "pending");
      return {
        ...d,
        dateDeclared: d.createdAt,
        loanLabel: d.loan ? `Loan #${d.loan.id}` : "General Payment",
        amountValue: safeNumber(d.paymentAmount),
        deductionMonthLabel: MONTHS[d.month] ? `${MONTHS[d.month]} ${d.year}` : "—",
        deductionDateLabel: d.deductionDate || "",
        statusGroup,
        statusLabel: d.isProcessed ? "Paid" : d.groupLeaderApproved ? "Approved" : "Pending",
        approvedBy: d.groupLeaderApprovedBy || "—",
        approvalDate: d.groupLeaderApprovedAt || "",
      };
    });
  }, [paymentHistoryRaw]);

  const filteredHistory = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    const statusFilter = historyColumnFilters.find((f) => f.id === "statusGroup")?.value as string[] | undefined;

    const list = historyRowData.filter((row: any) => {
      const matchesSearch = !q || [
        row.loanLabel, row.statusLabel, row.approvedBy,
        row.amountValue, row.deductionMonthLabel,
      ].join(" ").toLowerCase().includes(q);
      const matchesStatus = !statusFilter?.length || statusFilter.includes(row.statusGroup);
      return matchesSearch && matchesStatus;
    });

    const sort = historySorting[0] || { id: "createdAt", desc: true };
    return [...list].sort((a: any, b: any) => {
      let av = a[sort.id];
      let bv = b[sort.id];
      if (["createdAt", "dateDeclared", "approvalDate", "deductionDateLabel"].includes(sort.id)) {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      }
      if (typeof av === "number" && typeof bv === "number") return sort.desc ? bv - av : av - bv;
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av === bv) return 0;
      return sort.desc ? (av > bv ? -1 : 1) : (av > bv ? 1 : -1);
    });
  }, [historyRowData, historySearch, historyColumnFilters, historySorting]);

  const historyPageCount = Math.max(1, Math.ceil(filteredHistory.length / historyPagination.pageSize));
  const paginatedHistory = useMemo(() => {
    const safePageIndex = Math.min(historyPagination.pageIndex, historyPageCount - 1);
    const start = safePageIndex * historyPagination.pageSize;
    return filteredHistory.slice(start, start + historyPagination.pageSize);
  }, [filteredHistory, historyPageCount, historyPagination.pageIndex, historyPagination.pageSize]);

  // ── Column Definitions: Active Loans ─────────────────────────────────
  const loansColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "id",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Loan" />,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">#{row.original.id}</span>
            <StatusBadge status={row.original.statusLabel} size="sm" />
          </div>
        ),
      },
      {
        accessorKey: "purposeLabel",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Loan Type" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm text-slate-600">{row.original.purposeLabel}</span>,
      },
      {
        accessorKey: "amountValue",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Requested" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm font-medium text-slate-700">{row.original.amountValue.toLocaleString()} FRW</span>,
      },
      {
        accessorKey: "disbursedValue",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Disbursed" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm text-slate-600">{row.original.disbursedValue.toLocaleString()} FRW</span>,
      },
      {
        accessorKey: "remainingValue",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Outstanding" />,
        cell: ({ row }) => (
          <span className="inline-flex items-center rounded-lg bg-orange-50 border border-orange-200 px-2 py-0.5 text-xs font-bold text-orange-700">
            {row.original.remainingValue.toLocaleString()} FRW
          </span>
        ),
      },
      {
        accessorKey: "totalPaidValue",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Paid" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm font-semibold text-emerald-600">{row.original.totalPaidValue.toLocaleString()} FRW</span>,
      },
      {
        accessorKey: "progressValue",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Progress" />,
        cell: ({ row }) => {
          const progress = row.original.progressValue;
          return (
            <div className="min-w-[100px] space-y-1">
              <div className="flex justify-between text-[11px]">
                <span className="text-slate-400">Repayment</span>
                <span className="font-semibold text-slate-700">{progress.toFixed(1)}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={cn("h-full rounded-full transition-all duration-500", progress >= 75 ? "bg-emerald-500" : progress >= 40 ? "bg-blue-500" : "bg-amber-500")}
                  style={{ width: `${Math.min(100, progress)}%` }}
                />
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "monthlyValue",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Monthly" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm font-semibold text-slate-900">{row.original.monthlyValue.toLocaleString()} FRW</span>,
      },
      {
        accessorKey: "nextDateLabel",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Next Payment" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm text-slate-600">{row.original.nextDateLabel}</span>,
      },
      {
        id: "actions",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 px-2 text-xs rounded-lg gap-1",
              row.original.isSelected
                ? "text-emerald-600 bg-emerald-50 hover:bg-emerald-100"
                : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            )}
            onClick={() => {
              if (!hasExistingDeclaration) {
                setSelectedLoanId(row.original.id);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            disabled={hasExistingDeclaration}
          >
            {row.original.isSelected ? "Selected" : "Select"}
          </Button>
        ),
      },
    ],
    [hasExistingDeclaration]
  );

  const loansFacets = useMemo(() => [
    {
      name: "statusGroup",
      title: "Status",
      type: "checkbox",
      options: [
        { label: "Active", value: "active" },
        { label: "Approved", value: "approved" },
      ],
    },
  ], []);

  // ── Column Definitions: Payment History ──────────────────────────────
  const historyColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "dateDeclared",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Date Declared" />,
        cell: ({ row }) => {
          const value = row.original.dateDeclared;
          return <span className="whitespace-nowrap text-sm text-slate-600">{value ? new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</span>;
        },
      },
      {
        accessorKey: "loanLabel",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Loan" />,
        cell: ({ row }) => (
          <span className="inline-flex items-center rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
            {row.original.loanLabel}
          </span>
        ),
      },
      {
        accessorKey: "amountValue",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Declared Amount" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm font-bold text-slate-900">{row.original.amountValue.toLocaleString()} FRW</span>,
      },
      {
        accessorKey: "deductionMonthLabel",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Deduction Month" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm text-slate-600">{row.original.deductionMonthLabel}</span>,
      },
      {
        accessorKey: "deductionDateLabel",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Deduction Date" />,
        cell: ({ row }) => {
          const value = row.original.deductionDateLabel;
          return <span className="whitespace-nowrap text-sm text-slate-600">{value ? new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}</span>;
        },
      },
      {
        accessorKey: "statusGroup",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
        cell: ({ row }) => <StatusBadge status={row.original.statusLabel} size="sm" />,
      },
      {
        accessorKey: "approvedBy",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Approved By" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm text-slate-600">{row.original.approvedBy}</span>,
      },
      {
        accessorKey: "approvalDate",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Approval Date" />,
        cell: ({ row }) => {
          const value = row.original.approvalDate;
          return <span className="whitespace-nowrap text-sm text-slate-600">{value ? new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</span>;
        },
      },
    ],
    []
  );

  const historyFacets = useMemo(() => [
    {
      name: "statusGroup",
      title: "Status",
      type: "checkbox",
      options: [
        { label: "Pending", value: "pending" },
        { label: "Approved", value: "approved" },
        { label: "Rejected", value: "rejected" },
      ],
    },
  ], []);

  // ── Loading Skeleton ─────────────────────────────────────────────────
  if (loadingLoans) {
    return (
    <div className="space-y-6">
        <Skeleton className="h-5 w-48 rounded-lg" />
        <Skeleton className="h-8 w-64 rounded-lg" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-[108px] rounded-[16px]" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Skeleton className="h-[400px] rounded-[16px]" />
          <Skeleton className="h-[400px] rounded-[16px]" />
        </div>
        <Skeleton className="h-80 rounded-[16px]" />
        <Skeleton className="h-80 rounded-[16px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── PAGE HEADER ─────────────────────────────────────────────── */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Declare Loan Payment</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Choose a loan and declare how much should be deducted from your salary.
        </p>
      </div>

      {/* ── EXISTING DECLARATIONS ALERT ─────────────────────────────── */}
      {hasExistingDeclaration && declarationStatus?.declarations && declarationStatus.declarations.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Alert className={cn("rounded-[16px] border", declarationStatus.hasPendingDeclaration ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50")}>
            {declarationStatus.hasPendingDeclaration ? (
              <Clock className="h-4 w-4 text-amber-600" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            )}
            <AlertDescription className={declarationStatus.hasPendingDeclaration ? "text-amber-800" : "text-emerald-800"}>
              {declarationStatus.hasPendingDeclaration ? (
                <>
                  <strong>Declaration(s) Pending Approval</strong>
                  <p className="text-sm mt-1">
                    You have {declarationStatus.declarations.filter((d: any) => !d.groupLeaderApproved).length} pending declaration(s)
                    totaling {declarationStatus.declarations.filter((d: any) => !d.groupLeaderApproved).reduce((sum: number, d: any) => sum + d.paymentAmount, 0).toLocaleString()} FRW for {MONTHS[selectedMonth]} {selectedYear}.
                  </p>
                </>
              ) : (
                <>
                  <strong>All Declarations Approved</strong>
                  <p className="text-sm mt-1">
                    All your {declarationStatus.declarations.length} declaration(s) totaling {declarationStatus.totalDeclaredAmount.toLocaleString()} FRW for {MONTHS[selectedMonth]} {selectedYear} have been approved.
                  </p>
                </>
              )}
            </AlertDescription>
          </Alert>
        </motion.div>
      )}

      {/* ── SUMMARY CARDS ───────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-emerald-200 bg-emerald-50/40 shadow-sm rounded-[16px] overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-100 border border-emerald-200">
                <Wallet className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-emerald-700">Active Loans</p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight">{activeLoans.length} <span className="text-sm font-medium text-slate-500">active</span></p>
                <p className="text-[11px] text-slate-500 mt-0.5">Currently active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-blue-200 bg-blue-50/40 shadow-sm rounded-[16px] overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 border border-blue-200">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-blue-700">Outstanding Balance</p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight">{totalOutstanding.toLocaleString()} <span className="text-sm font-medium text-slate-500">FRW</span></p>
                <p className="text-[11px] text-slate-500 mt-0.5">Total remaining</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-purple-200 bg-purple-50/40 shadow-sm rounded-[16px] overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-purple-100 border border-purple-200">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-purple-700">Next Payment Date</p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight">{nextPaymentDate}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Upcoming deduction</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-orange-200 bg-orange-50/40 shadow-sm rounded-[16px] overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-orange-100 border border-orange-200">
                <Coins className="h-6 w-6 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-orange-700">Monthly Installment</p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight">{monthlyInstallment.toLocaleString()} <span className="text-sm font-medium text-slate-500">FRW</span></p>
                <p className="text-[11px] text-slate-500 mt-0.5">{selectedLoan ? "Selected loan" : "Per loan"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── TWO-COLUMN SECTION ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* LEFT: Declaration Form */}
        <Card className="border border-slate-200 shadow-sm rounded-[16px]">
          <CardHeader className="px-5 pt-5 pb-0">
            <CardTitle className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#009A44]/10">
                <CreditCard className="h-3.5 w-3.5 text-[#009A44]" />
              </div>
              Declare Loan Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Select Loan */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Select Loan <span className="text-red-500">*</span></Label>
                {activeLoans.length > 0 ? (
                  <>
                    <Select
                      value={selectedLoanId?.toString() || "all"}
                      onValueChange={(value) => setSelectedLoanId(value === "all" ? null : parseInt(value))}
                    >
                      <SelectTrigger className="h-10 text-sm rounded-[10px]">
                        <SelectValue placeholder="Choose a loan" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Loans (General Payment)</SelectItem>
                        {activeLoans.map((loan: any) => {
                          const remaining = getLoanRemaining(loan);
                          return (
                            <SelectItem key={loan.id} value={loan.id.toString()}>
                              Loan #{loan.id} — {remaining.toLocaleString()} FRW remaining
                              {loan.purpose ? ` (${loan.purpose})` : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {selectedLoan && (
                      <div className="rounded-[10px] border border-blue-200 bg-blue-50 p-3 mt-1.5">
                        <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider mb-1.5">Selected Loan Details</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div><span className="text-blue-400">Loan #</span><span className="ml-1 font-semibold text-blue-900">{selectedLoan.id}</span></div>
                          <div><span className="text-blue-400">Purpose</span><span className="ml-1 font-semibold text-blue-900">{selectedLoan.purpose || "General"}</span></div>
                          <div><span className="text-blue-400">Monthly</span><span className="ml-1 font-semibold text-blue-900">{monthlyInstallment.toLocaleString()} FRW</span></div>
                          <div><span className="text-blue-400">Remaining</span><span className="ml-1 font-semibold text-blue-900">{maxPaymentAmount.toLocaleString()} FRW</span></div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-3 rounded-[10px] border border-slate-200 bg-slate-50 p-4">
                    <AlertCircle className="h-5 w-5 text-slate-400 shrink-0" />
                    <p className="text-sm text-slate-500">No active loans available.</p>
                  </div>
                )}
              </div>

              {/* Payment Amount */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-slate-600">Payment Amount (FRW) <span className="text-red-500">*</span></Label>
                  <span className="text-[10px] text-slate-400 font-medium">Max: {maxPaymentAmount.toLocaleString()} FRW</span>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">FRW</span>
                  <Input
                    type="number"
                    value={paymentAmount}
                    onChange={handlePaymentAmountChange}
                    onBlur={() => {
                      if (paymentAmount) {
                        const numValue = parseFloat(paymentAmount);
                        if (!isNaN(numValue) && numValue > maxPaymentAmount) {
                          setPaymentAmount(maxPaymentAmount.toString());
                          setPaymentAmountError("");
                        }
                      }
                    }}
                    placeholder="0"
                    min="0"
                    max={maxPaymentAmount}
                    step="1"
                    disabled={declareMutation.isLoading}
                    className={cn(
                      "h-10 text-sm rounded-[10px] pl-10 font-semibold",
                      paymentAmountError && "border-red-300 focus-visible:ring-red-500/20"
                    )}
                    required
                  />
                </div>
                {paymentAmountError && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-0.5">
                    <AlertCircle className="h-3 w-3" />
                    {paymentAmountError}
                  </p>
                )}

                {/* Quick Amount Chips */}
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {[
                    { label: "25%", pct: 0.25 },
                    { label: "50%", pct: 0.50 },
                    { label: "75%", pct: 0.75 },
                    { label: "Pay Full", pct: 1 },
                  ].map(({ label, pct }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => {
                        const amt = Math.round(maxPaymentAmount * pct);
                        setPaymentAmount(amt.toString());
                        setPaymentAmountError("");
                      }}
                      className={cn(
                        "rounded-[10px] border px-3 py-1 text-[11px] font-semibold transition-all duration-200",
                        paymentAmount && parseFloat(paymentAmount) === Math.round(maxPaymentAmount * pct)
                          ? "border-[#009A44]/30 bg-[#009A44]/10 text-[#009A44]"
                          : "border-slate-200 bg-white text-slate-600 hover:border-[#009A44]/20 hover:bg-[#009A44]/5 hover:text-[#009A44]"
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Deduction Month */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Salary Deduction Month</Label>
                <Select value={deductionMonth} onValueChange={setDeductionMonth}>
                  <SelectTrigger className="h-10 text-sm rounded-[10px]">
                    <SelectValue placeholder={`Select month (current: ${MONTHS[selectedMonth]})`} />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month, idx) => (
                      <SelectItem key={month} value={month}>
                        {month} {idx === selectedMonth ? "(Current)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Deduction Date */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Deduction Date</Label>
                <Input
                  type="date"
                  value={deductionDate}
                  onChange={(e) => setDeductionDate(e.target.value)}
                  className="h-10 text-sm rounded-[10px]"
                />
              </div>

              {/* Optional Note */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Optional Note <span className="text-slate-400 font-normal">(optional)</span></Label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add any notes for this payment..."
                  rows={3}
                  className="flex w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#009A44]/20 focus:border-[#009A44] transition-all resize-none"
                />
              </div>

              {/* Info Alert */}
              <div className="bg-blue-50 border border-blue-100 rounded-[10px] px-4 py-2.5 flex items-start gap-2.5">
                <Info className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-600 leading-relaxed">
                  This amount will be deducted from your salary after approval by your group leader.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClear}
                  disabled={declareMutation.isLoading}
                  className="h-10 px-5 text-sm gap-1.5 rounded-[10px] border-slate-200"
                >
                  <RotateCcw className="h-4 w-4" />
                  Clear
                </Button>
                <Button
                  type="submit"
                  disabled={!canDeclareWithLoans || declareMutation.isLoading}
                  className="h-10 px-5 text-sm gap-1.5 rounded-[10px] bg-[#009A44] hover:bg-[#00803a] text-white font-medium"
                >
                  {declareMutation.isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  Declare Payment
                </Button>
              </div>

              {!canDeclareWithLoans && !hasActiveLoans && (
                <Alert className="rounded-[10px] border-slate-200">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    You cannot declare a payment at this time. Please contact your group leader.
                  </AlertDescription>
                </Alert>
              )}
            </form>
          </CardContent>
        </Card>

        {/* RIGHT: Payment Preview */}
        <Card className="border border-slate-200 shadow-sm rounded-[16px]">
          <CardHeader className="px-5 pt-5 pb-0">
            <CardTitle className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#009A44]/10">
                <TrendingUp className="h-3.5 w-3.5 text-[#009A44]" />
              </div>
              Payment Preview
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            {/* Status Badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-xs font-semibold text-amber-700">
                <Clock className="h-3 w-3" />
                Pending Approval
              </span>
            </div>

            {/* Preview Details */}
            <div className="space-y-2.5 mb-5">
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-xs text-slate-500">Selected Loan</span>
                <span className="text-sm font-semibold text-slate-900">{selectedLoan ? `Loan #${selectedLoan.id}` : "All Loans"}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-xs text-slate-500">Loan Number</span>
                <span className="text-sm font-semibold text-slate-900">{selectedLoan ? `#${selectedLoan.id}` : "—"}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-xs text-slate-500">Outstanding Balance</span>
                <span className="text-sm font-semibold text-slate-900">{selectedLoan ? `${maxPaymentAmount.toLocaleString()} FRW` : `${totalOutstanding.toLocaleString()} FRW`}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-xs text-slate-500">Declared Payment</span>
                <span className={cn("text-sm font-bold", declaredAmount > 0 ? "text-[#009A44]" : "text-slate-400")}>
                  {declaredAmount > 0 ? `${declaredAmount.toLocaleString()} FRW` : "0 FRW"}
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-xs text-slate-500">Balance After Payment</span>
                <span className={cn("text-sm font-bold", balanceAfter >= 0 ? "text-slate-900" : "text-red-600")}>
                  {balanceAfter >= 0 ? `${balanceAfter.toLocaleString()} FRW` : "0 FRW"}
                </span>
              </div>
            </div>

            {/* Repayment Progress */}
            {selectedLoan && (
              <div className="mb-5">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-xs font-medium text-slate-600">Repayment Progress</span>
                  <span className="text-xs font-semibold text-[#009A44]">{repaymentProgress.toFixed(1)}%</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-[#009A44] rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, repaymentProgress)}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>Paid: {paidAmount.toLocaleString()} FRW</span>
                  <span>Total: {loanAmount.toLocaleString()} FRW</span>
                </div>
              </div>
            )}

            {/* Statistics */}
            {selectedLoan && (
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-slate-50 rounded-[12px] p-3.5">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Total Loan</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">{loanAmount.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">FRW</p>
                </div>
                <div className="bg-slate-50 rounded-[12px] p-3.5">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Paid Amount</p>
                  <p className="text-lg font-bold text-[#009A44] mt-1">{paidAmount.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">FRW</p>
                </div>
                <div className="bg-slate-50 rounded-[12px] p-3.5">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Remaining</p>
                  <p className="text-lg font-bold text-orange-600 mt-1">{maxPaymentAmount.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">FRW</p>
                </div>
              </div>
            )}

            {/* Salary Deduction Info */}
            <div className="bg-slate-50 rounded-[12px] p-4 space-y-3">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Salary Deduction Info</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-[10px] border border-slate-200 p-3">
                  <p className="text-[10px] text-slate-400 font-medium">Deduction Month</p>
                  <p className="text-sm font-bold text-slate-900 mt-0.5">
                    {deductionMonth || MONTHS[selectedMonth]} {selectedYear}
                  </p>
                </div>
                <div className="bg-white rounded-[10px] border border-slate-200 p-3">
                  <p className="text-[10px] text-slate-400 font-medium">Deduction Date</p>
                  <p className="text-sm font-bold text-slate-900 mt-0.5">
                    {deductionDate ? new Date(deductionDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "Not set"}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── ACTIVE LOANS TABLE ──────────────────────────────────────── */}
      {activeLoans.length > 0 && (
        <Card className="border border-slate-200 shadow-sm rounded-[16px]">
          <CardHeader className="px-5 pt-5 pb-0">
            <CardTitle className="text-[15px] font-semibold text-slate-900">Your Active Loans</CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-3">
            <DataTable
              columns={loansColumns}
              data={paginatedLoans}
              facets={loansFacets}
              isLoading={loadingLoans}
              isFetching={fetchingLoans}
              onSearch={(value: string) => {
                setLoansSearch(value);
                setLoansPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              columnFilters={loansColumnFilters}
              setColumnFilters={(next) => {
                setLoansColumnFilters(next);
                setLoansPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              sorting={loansSorting}
              setSorting={setLoansSorting}
              setPagination={setLoansPagination}
              pageIndex={Math.min(loansPagination.pageIndex, loansPageCount - 1)}
              pageSize={loansPagination.pageSize}
              pageCount={loansPageCount}
              enableExport={false}
              emptyMessage="No active loans found"
              emptyDescription="You don't have any active loans to declare payments for."
              loadingRows={5}
            />
          </CardContent>
        </Card>
      )}

      {/* ── PAYMENT HISTORY TABLE ───────────────────────────────────── */}
      <Card className="border border-slate-200 shadow-sm rounded-[16px]">
        <CardHeader className="px-5 pt-5 pb-0">
          <CardTitle className="text-[15px] font-semibold text-slate-900">Payment Declaration History</CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-3">
          <DataTable
            columns={historyColumns}
            data={paginatedHistory}
            facets={historyFacets}
            isLoading={loadingHistory}
            isFetching={fetchingHistory}
            onSearch={(value: string) => {
              setHistorySearch(value);
              setHistoryPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            columnFilters={historyColumnFilters}
            setColumnFilters={(next) => {
              setHistoryColumnFilters(next);
              setHistoryPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            sorting={historySorting}
            setSorting={setHistorySorting}
            setPagination={setHistoryPagination}
            pageIndex={Math.min(historyPagination.pageIndex, historyPageCount - 1)}
            pageSize={historyPagination.pageSize}
            pageCount={historyPageCount}
            enableExport={false}
            emptyMessage="No payment declarations found"
            emptyDescription="Your declaration history will appear here."
            loadingRows={5}
          />
        </CardContent>
      </Card>
    </div>
  );
}
